import type { SavedCircuit } from './api.js';
import { drawCircuitInCanvas } from './canvasPreview.js';
import { mountCircuit3dPreview, type Circuit3dPreviewHandle } from './circuit3dPreview.js';
import { createPillButton } from '../ui/pillButton.js';
import { renameCircuit } from './api.js';
import {
  circuitDisplayTitle,
  MAX_CIRCUIT_NAME_LENGTH,
  normalizeCircuitName,
} from './circuitDisplay.js';
import { applyRoleVisibility } from '../auth/navAccount.js';

export type DetailViewHandlers = {
  onDelete?: (circuit: SavedCircuit) => void;
  onRenamed?: (circuit: SavedCircuit) => void;
  onExport?: (circuit: SavedCircuit) => void;
  /** Importe un fichier de sauvegarde (un ou plusieurs circuits) — action globale
   * déclenchée depuis le détail d'un circuit plutôt que sans contexte en haut de la
   * liste, mais crée toujours de nouveaux circuits (voir `POST /api/circuits/import`),
   * sans lien avec celui affiché. */
  onImport?: () => void;
  onEdit3D?: (circuit: SavedCircuit) => void;
  /** Admin uniquement : boutons de validation affichés si le circuit est `pending`. */
  onApprove?: (circuit: SavedCircuit) => void;
  onReject?: (circuit: SavedCircuit) => void;
  /** Admin uniquement : exclut/réintègre le circuit de la rotation des saisons sans
   * toucher à son historique de replays (voir `onDelete`, qui les efface). */
  onArchive?: (circuit: SavedCircuit) => void;
  onUnarchive?: (circuit: SavedCircuit) => void;
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'En attente de validation',
  rejected: 'Rejeté',
  archived: 'Archivé — hors rotation des saisons',
};

/** Aperçu 3D interactif actif dans le panneau détail — démonté avant tout re-rendu. */
let active3dPreview: Circuit3dPreviewHandle | null = null;

export function renderDetail(
  container: HTMLElement,
  circuit: SavedCircuit | null,
  handlers?: DetailViewHandlers
): void {
  active3dPreview?.dispose();
  active3dPreview = null;
  container.innerHTML = '';

  if (circuit === null) {
    container.innerHTML = `
      <div class="circuits-detail-empty">
        <p>Sélectionnez un circuit dans la liste pour afficher son détail.</p>
      </div>`;
    return;
  }

  const date = new Date(circuit.created_at);
  const dateStr = date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const title = circuitDisplayTitle(circuit);
  const hasCustomName = Boolean(circuit.name?.trim());
  const isPending = circuit.status === 'pending';
  const isArchived = circuit.status === 'archived';
  // Un circuit `pending`/`rejected` n'a jamais été en rotation — l'archivage n'a de
  // sens que pour en retirer un qui l'était (`published`, ou son défaut implicite).
  const isArchivable = !isPending && !isArchived;
  const hasHeaderActions = Boolean(
    handlers?.onEdit3D ||
    handlers?.onExport ||
    handlers?.onImport ||
    handlers?.onDelete ||
    (isPending && (handlers?.onApprove || handlers?.onReject)) ||
    (isArchivable && handlers?.onArchive) ||
    (isArchived && handlers?.onUnarchive)
  );
  const statusLabel = circuit.status ? STATUS_LABELS[circuit.status] : undefined;

  const section = document.createElement('div');
  section.className = 'circuits-detail';
  section.innerHTML = `
    <header class="circuits-detail-header">
      <div class="circuits-detail-header-main">
        <div class="circuits-detail-title-row">
          <h2 class="circuits-detail-title">${escapeHtml(title)}</h2>
          <button type="button" class="circuits-detail-rename-trigger" aria-label="Renommer le circuit" title="Renommer">✎</button>
          ${statusLabel ? `<span class="circuits-detail-status-badge circuits-detail-status-badge--${circuit.status}">${statusLabel}</span>` : ''}
        </div>
        ${hasCustomName ? `<span class="circuits-detail-id">#${circuit.id}</span>` : ''}
        <time class="circuits-detail-date" datetime="${circuit.created_at}">${dateStr}</time>
        <p class="circuits-detail-rename-status" role="status" hidden></p>
      </div>
      ${hasHeaderActions ? '<div class="circuits-detail-header-actions"></div>' : ''}
    </header>
    <div class="circuits-detail-body">
      <div class="circuits-detail-preview-wrap">
        <div class="circuits-detail-3d"></div>
        <canvas class="circuits-detail-canvas" width="400" height="300" hidden></canvas>
        <button type="button" class="circuits-detail-2d-toggle" aria-pressed="false">Voir en 2D</button>
      </div>
      <dl class="circuits-detail-params">
        <dt>Nom</dt>
        <dd class="circuits-detail-name-value">${escapeHtml(circuit.name?.trim() || '—')}</dd>
        <dt>Lignes droites</dt>
        <dd>${circuit.params.numStraights}</dd>
        <dt>Virages</dt>
        <dd>${circuit.params.numTurns}</dd>
        <dt>Nombre de cases</dt>
        <dd>${circuit.params.approximateCells}</dd>
        <dt>Longueur du chemin</dt>
        <dd>${circuit.circuit.path.length} cases</dd>
        <dt>Dimensions grille</dt>
        <dd>${circuit.circuit.width} × ${circuit.circuit.height}</dd>
      </dl>
    </div>`;

  const preview3dEl = section.querySelector('.circuits-detail-3d') as HTMLElement;
  const canvas = section.querySelector('.circuits-detail-canvas') as HTMLCanvasElement;
  const toggle2dBtn = section.querySelector('.circuits-detail-2d-toggle') as HTMLButtonElement;
  if (preview3dEl && circuit.circuit.path.length > 0) {
    active3dPreview = mountCircuit3dPreview(preview3dEl, circuit.circuit, undefined, {
      circuitName: circuit.name,
    });
  }
  if (canvas && toggle2dBtn) {
    let showing3d = true;
    toggle2dBtn.addEventListener('click', () => {
      showing3d = !showing3d;
      preview3dEl.hidden = !showing3d;
      canvas.hidden = showing3d;
      toggle2dBtn.textContent = showing3d ? 'Voir en 2D' : 'Voir en 3D';
      toggle2dBtn.setAttribute('aria-pressed', String(!showing3d));
      if (!showing3d) {
        drawCircuitInCanvas(canvas, circuit.circuit, { viewportWidth: 400, viewportHeight: 300 });
      }
    });
  }

  const actionSlot = section.querySelector('.circuits-detail-header-actions');
  if (actionSlot && handlers?.onEdit3D) {
    const edit3dBtn = createPillButton('Éditeur de circuit', {
      variant: 'soft',
      onClick: () => handlers.onEdit3D!(circuit),
    });
    edit3dBtn.dataset.role = 'admin';
    actionSlot.appendChild(edit3dBtn);
  }
  if (actionSlot && handlers?.onExport) {
    actionSlot.appendChild(
      createPillButton('Exporter', {
        variant: 'soft',
        onClick: () => handlers.onExport!(circuit),
      })
    );
  }
  if (actionSlot && handlers?.onImport) {
    const importBtn = createPillButton('Importer', {
      variant: 'soft',
      onClick: () => handlers.onImport!(),
    });
    importBtn.dataset.role = 'admin,moderator';
    actionSlot.appendChild(importBtn);
  }
  if (actionSlot && isPending && handlers?.onApprove) {
    const approveBtn = createPillButton('Approuver', {
      variant: 'cta',
      onClick: () => handlers.onApprove!(circuit),
    });
    approveBtn.dataset.role = 'admin';
    actionSlot.appendChild(approveBtn);
  }
  if (actionSlot && isPending && handlers?.onReject) {
    const rejectBtn = createPillButton('Rejeter', {
      variant: 'soft',
      onClick: () => handlers.onReject!(circuit),
    });
    rejectBtn.dataset.role = 'admin';
    actionSlot.appendChild(rejectBtn);
  }
  if (actionSlot && isArchivable && handlers?.onArchive) {
    const archiveBtn = createPillButton('Archiver', {
      variant: 'soft',
      onClick: () => handlers.onArchive!(circuit),
    });
    archiveBtn.dataset.role = 'admin';
    actionSlot.appendChild(archiveBtn);
  }
  if (actionSlot && isArchived && handlers?.onUnarchive) {
    const unarchiveBtn = createPillButton('Désarchiver', {
      variant: 'soft',
      onClick: () => handlers.onUnarchive!(circuit),
    });
    unarchiveBtn.dataset.role = 'admin';
    actionSlot.appendChild(unarchiveBtn);
  }
  if (actionSlot && handlers?.onDelete) {
    const deleteBtn = createPillButton('Supprimer', {
      variant: 'soft',
      class: 'circuits-detail-delete-btn',
      onClick: () => handlers.onDelete!(circuit),
    });
    deleteBtn.dataset.role = 'admin';
    actionSlot.appendChild(deleteBtn);
  }

  mountRenameControl(section, circuit, container, handlers);

  container.appendChild(section);
  applyRoleVisibility();
}

function mountRenameControl(
  section: HTMLElement,
  circuit: SavedCircuit,
  container: HTMLElement,
  handlers?: DetailViewHandlers
): void {
  const titleRow = section.querySelector('.circuits-detail-title-row') as HTMLElement;
  const trigger = section.querySelector('.circuits-detail-rename-trigger') as HTMLButtonElement;
  const status = section.querySelector('.circuits-detail-rename-status') as HTMLElement;

  if (!titleRow || !trigger) return;

  trigger.addEventListener('click', () => {
    if (titleRow.querySelector('.circuits-detail-rename-form')) return;

    status.hidden = true;
    status.textContent = '';

    const current = circuit.name?.trim() ?? '';
    titleRow.innerHTML = `
      <form class="circuits-detail-rename-form">
        <label class="visually-hidden" for="circuits-rename-input">Nom du circuit</label>
        <input
          id="circuits-rename-input"
          class="circuits-detail-rename-input"
          type="text"
          maxlength="${MAX_CIRCUIT_NAME_LENGTH}"
          value="${escapeHtml(current)}"
          placeholder="Ex. Monaco, Ring des Nürb…"
          autocomplete="off"
        />
        <div class="circuits-detail-rename-form-actions"></div>
      </form>`;

    const form = titleRow.querySelector('.circuits-detail-rename-form') as HTMLFormElement;
    const input = titleRow.querySelector('#circuits-rename-input') as HTMLInputElement;
    const actions = titleRow.querySelector('.circuits-detail-rename-form-actions') as HTMLElement;

    actions.appendChild(
      createPillButton('Enregistrer', {
        variant: 'cta',
        small: true,
        onClick: () => form.requestSubmit(),
      })
    );
    actions.appendChild(
      createPillButton('Annuler', {
        variant: 'soft',
        small: true,
        onClick: () => renderDetail(container, circuit, handlers),
      })
    );

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      void (async () => {
        try {
          const normalized = normalizeCircuitName(input.value);
          if (!normalized) {
            status.hidden = false;
            status.className = 'circuits-detail-rename-status circuits-detail-rename-status--error';
            status.textContent = 'Entrez un nom (1–80 caractères).';
            return;
          }
          status.hidden = false;
          status.className = 'circuits-detail-rename-status';
          status.textContent = 'Enregistrement…';

          const updated = await renameCircuit(circuit.id, normalized);
          handlers?.onRenamed?.(updated);
          renderDetail(container, updated, handlers);
        } catch (err) {
          status.hidden = false;
          status.className = 'circuits-detail-rename-status circuits-detail-rename-status--error';
          status.textContent = err instanceof Error ? err.message : 'Échec du renommage.';
        }
      })();
    });

    input.focus();
    input.select();
  });
}

export function setDetailLoading(container: HTMLElement, loading: boolean): void {
  if (loading) {
    active3dPreview?.dispose();
    active3dPreview = null;
    container.innerHTML =
      '<div class="circuits-loading"><span class="spinner"></span><p>Chargement du circuit…</p></div>';
  }
}

export function setDetailError(container: HTMLElement, message: string): void {
  active3dPreview?.dispose();
  active3dPreview = null;
  container.innerHTML = `
    <div class="circuits-error">
      <span class="circuits-error-icon" aria-hidden="true">⚠️</span>
      <p>${escapeHtml(message)}</p>
    </div>`;
}

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}
